import { changeBetween } from './sales-metrics.js';
/**
 * Website traffic and conversion, defined once like the sales metrics.
 *
 * Conversion is the websites' own orders divided by Google Analytics
 * sessions, for the same sites and the same days — never marketplace orders,
 * which GA never saw, and never GA's own purchase count, which misses orders
 * whenever tracking is blocked or broken. GA's purchases and revenue are
 * used only to check the tracking (capture rate).
 *
 * Funnel and product figures need the site to send GA4 ecommerce events;
 * without them they are null rather than a misleading zero.
 */
export const FUNNEL_EVENTS = ['session_start', 'view_item', 'add_to_cart', 'begin_checkout', 'purchase'];
export const FUNNEL_LABELS = {
    session_start: 'Visits',
    view_item: 'Viewed a product',
    add_to_cart: 'Added to cart',
    begin_checkout: 'Started checkout',
    purchase: 'Purchased',
};
export const emptyFunnel = () => ({
    session_start: 0,
    view_item: 0,
    add_to_cart: 0,
    begin_checkout: 0,
    purchase: 0,
});
export const EMPTY_TRAFFIC_TOTALS = {
    sessions: 0,
    views: 0,
    addToCarts: 0,
    checkouts: 0,
    gaPurchases: 0,
    gaRevenueMinor: 0,
    funnel: emptyFunnel(),
    itemViews: 0,
    itemPurchases: 0,
    orders: 0,
    netSalesMinor: 0,
    units: 0,
    ecommerce: false,
    days: 0,
};
export const TRAFFIC_METRIC_IDS = [
    'site_conversion',
    'sessions',
    'revenue_per_session',
    'add_to_cart_rate',
    'checkout_rate',
    'ga_capture',
    'item_view_to_purchase',
];
const ratio = (a, b) => (b > 0 ? a / b : null);
export const TRAFFIC_METRICS = {
    site_conversion: {
        id: 'site_conversion',
        label: 'Site conversion',
        format: 'percent',
        better: 'up',
        description: 'Website orders ÷ website visits (GA sessions), same sites and days.',
        value: (t) => ratio(t.orders, t.sessions),
    },
    sessions: {
        id: 'sessions',
        label: 'Visits',
        format: 'count',
        better: 'up',
        description: 'Website sessions, as Google Analytics counts them.',
        value: (t) => t.sessions,
    },
    revenue_per_session: {
        id: 'revenue_per_session',
        label: 'Sales per visit',
        format: 'money',
        better: 'up',
        description: 'Website net sales ÷ visits.',
        value: (t) => ratio(t.netSalesMinor, t.sessions),
    },
    add_to_cart_rate: {
        id: 'add_to_cart_rate',
        label: 'Add-to-cart rate',
        format: 'percent',
        better: 'up',
        description: 'Visits that added something to the cart. Needs GA4 ecommerce events.',
        value: (t) => (t.ecommerce ? ratio(t.funnel.add_to_cart, t.funnel.session_start || t.sessions) : null),
    },
    checkout_rate: {
        id: 'checkout_rate',
        label: 'Checkout rate',
        format: 'percent',
        better: 'up',
        description: 'Visits that started checkout. Needs GA4 ecommerce events.',
        value: (t) => (t.ecommerce ? ratio(t.funnel.begin_checkout, t.funnel.session_start || t.sessions) : null),
    },
    ga_capture: {
        id: 'ga_capture',
        label: 'GA tracking capture',
        format: 'percent',
        better: 'up',
        description: 'Purchases GA recorded ÷ website orders. Well under 100% means orders GA never saw (blockers, broken tracking).',
        value: (t) => (t.ecommerce ? ratio(t.gaPurchases, t.orders) : null),
    },
    item_view_to_purchase: {
        id: 'item_view_to_purchase',
        label: 'Sold per product view',
        format: 'percent',
        better: 'up',
        description: 'Website units sold ÷ product views in GA. Needs GA4 ecommerce events.',
        value: (t) => (t.ecommerce ? ratio(t.units, t.itemViews) : null),
    },
};
export const isTrafficMetricId = (value) => typeof value === 'string' && TRAFFIC_METRIC_IDS.includes(value);
export const trafficMetricValue = (id, t) => TRAFFIC_METRICS[id].value(t);
export const trafficMetricChange = (id, current, previous) => changeBetween(TRAFFIC_METRICS[id].better, current, previous);
/** Adds two sites' (or two days') traffic. Every part is a count, so plain sums are right. */
export function addTraffic(a, b) {
    const funnel = emptyFunnel();
    for (const step of FUNNEL_EVENTS)
        funnel[step] = a.funnel[step] + b.funnel[step];
    return {
        sessions: a.sessions + b.sessions,
        views: a.views + b.views,
        addToCarts: a.addToCarts + b.addToCarts,
        checkouts: a.checkouts + b.checkouts,
        gaPurchases: a.gaPurchases + b.gaPurchases,
        gaRevenueMinor: a.gaRevenueMinor + b.gaRevenueMinor,
        funnel,
        itemViews: a.itemViews + b.itemViews,
        itemPurchases: a.itemPurchases + b.itemPurchases,
        orders: a.orders + b.orders,
        netSalesMinor: a.netSalesMinor + b.netSalesMinor,
        units: a.units + b.units,
        ecommerce: a.ecommerce || b.ecommerce,
        days: Math.max(a.days, b.days),
    };
}
//# sourceMappingURL=traffic-metrics.js.map